import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as AlertIcon } from '../../assets/icons/alert-icon.svg';
import { ReactComponent as SuccessIcon } from '../../assets/icons/success-toast-icon.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/toaster-close-icon.svg';
import { brandMessage } from '../../config/branding';
import { useAppSelector } from '../../redux/hooks';
import { resetNotificationToInitialState } from '../../redux/slice/NotificationSlice';
import { removeCookie } from '../../shared-functions/Cookies';
import { getLocalStorageItem, removeLocalStorageItem } from '../../shared-functions/LocalStorage';

/**
 * Customized toast message - Can be triggered by changing redux state and also importing in a component
 */
function ToastMessage() {
  const navigate = useNavigate();
  const accountVerificationStatus = getLocalStorageItem('accountVerificationStatus');
  const [openToast, setOpenToast] = useState(false);
  const { type: notificationType, notificationDetails } = useAppSelector(
    (state) => state.notification,
  );
  const containerReference = useRef<HTMLDivElement>(null);
  const locate = useLocation();
  const currentPath = locate.pathname;
  const parts = currentPath.split('/');
  const slicedString = parts.slice(0, 3).join('/');

  /**
   * Handle click outside the toast container
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (containerReference.current && !containerReference.current.contains(event.target as Node)) {
      setOpenToast(false);
      resetNotificationToInitialState();
    }
  };

  /**
   * Function to close toast message
   */
  const handleClose = () => {
    setOpenToast(false);
    resetNotificationToInitialState();
  };

  useEffect(() => {
    // Add event listener for clicks
    document.addEventListener('mousedown', handleClickOutside);
    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (notificationType && notificationDetails.message) {
      setOpenToast(true);
    }

    if (notificationDetails.statusCode === 401 && accountVerificationStatus === 'approved') {
      removeCookie('ev-refresh-token');
      removeCookie('ev-access-token');
      removeLocalStorageItem('accountVerificationStatus');
      navigate('/'); // Navigate to login page if the user is unauthorized
    }
  }, [notificationType, notificationDetails]);

  useEffect(() => {
    if (openToast) {
      const timer = setTimeout(() => {
        setOpenToast(false);
        resetNotificationToInitialState();
      }, 3000);

      return () => clearTimeout(timer); // Cleanup the timer if the component unmounts
    }

    return undefined;
  }, [openToast]);

  return openToast
    ? ReactDOM.createPortal(
        <div className="toast-message-wrap">
          {(() => {
            let toastMessageContainer;
            if (notificationType === 'error') {
              toastMessageContainer = (
                <div
                  className={`toast-message-container ${notificationType}`}
                  ref={containerReference}>
                  <span>
                    <AlertIcon />
                  </span>
                  <p>
                    {(() => {
                      let messageToShow = notificationDetails.message;
                      if (
                        slicedString.includes('ev-admin') &&
                        (accountVerificationStatus === 'pending' ||
                          accountVerificationStatus === 'rejected')
                      ) {
                        messageToShow = brandMessage.toastMessage;
                      }
                      return messageToShow;
                    })()}
                  </p>
                  <span role="presentation" onClick={handleClose}>
                    <CloseIcon />
                  </span>
                </div>
              );
            } else if (notificationType === 'success') {
              toastMessageContainer = (
                <div className={`toast-message-container ${notificationType}`}>
                  <span>
                    <SuccessIcon />
                  </span>
                  <p>{notificationDetails.message}</p>
                  <span role="presentation" onClick={handleClose}>
                    <CloseIcon />
                  </span>
                </div>
              );
            }
            return toastMessageContainer;
          })()}
        </div>,
        document.body,
      )
    : null;
}

ToastMessage.defaultProps = {
  open: false,
  type: null, // Define more types if required
  statusCode: null,
  message: '',
  onClose: null,
  onCloseTimer: 6000,
};

export default ToastMessage;
